import { DurationHoursInput } from '@/components/molecules/DurationInput';
import { LiveNumberInput } from '@/components/molecules/LiveNumberInput';
import { pricesFromHours } from '@/lib/sales/pricingMode';
import { cn } from '@/lib/utils';

interface LineItemPricingControlsProps {
  type: string;
  priceStrategy?: string | null;
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  /** Hand-typed: it states its price, and T&M cannot rewrite it. */
  isCustom?: boolean;
  basePrice: number;
  laborRate?: number | null;
  estimatedHours?: number | null;
  className?: string;
  onUpdateBasePrice: (price: number) => void;
  onUpdateLaborRate: (rate: number) => void;
  onUpdateEstimatedHours: (hours: number) => void;
}

export function LineItemPricingControls({
  type,
  priceStrategy,
  pricingMode,
  isCustom,
  basePrice,
  laborRate,
  estimatedHours,
  className,
  onUpdateBasePrice,
  onUpdateLaborRate,
  onUpdateEstimatedHours,
}: LineItemPricingControlsProps) {
  const showEstimatedHours = pricesFromHours(
    { type, priceStrategy: priceStrategy ?? undefined, isCustom },
    pricingMode,
  );

  return (
    <div className={cn('flex min-w-0 items-center gap-1.5', className)}>
      {showEstimatedHours ? (
        <div className="flex items-center gap-1">
          {/* The box now reads "1h 30m", so the "h" that used to follow it
              would be a second, contradictory unit. Only the "×" is left. */}
          <DurationHoursInput
            value={estimatedHours}
            inputClassName="h-9 w-20 text-center text-xs"
            aria-label="Estimated hours"
            placeholder="0m"
            compact
            onChange={onUpdateEstimatedHours}
          />
          <span className="shrink-0 text-xs text-muted-foreground">×</span>
          <span className="shrink-0 text-xs text-muted-foreground">$</span>
          <LiveNumberInput
            value={laborRate ?? 0}
            step={0.5}
            className="h-9 w-14 text-center text-xs"
            aria-label="Hourly rate"
            onCommit={onUpdateLaborRate}
          />
          <span className="shrink-0 text-xs text-muted-foreground">/hr</span>
        </div>
      ) : (
        <LiveNumberInput
          value={basePrice}
          step={0.01}
          className="h-9 w-full min-w-20 text-right text-xs"
          aria-label="Unit price"
          onCommit={onUpdateBasePrice}
        />
      )}
    </div>
  );
}
